import React from 'react'
import { useDocumentTitle } from '@mantine/hooks'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { Navigate, Outlet, RouteProps, useLocation } from 'react-router-dom'
import { useAuth, useFeatureFlags } from '@node-space/hooks'
import { Box, Box as Main, Box as Page, Toaster } from '@node-space/ui-components'
import { AppBanners, AppBannerType } from 'components/app/AppBanners'
import { FlagEnabledBanner } from 'components/banners/FlagEnabledBanner'
import { SuspendedAccountBanner } from 'components/banners/SuspendedAccountBanner'
import { UKRiskBanner } from 'components/banners/UKRiskBanner'
import type { CustomBreadCrumbConfig } from 'components/Breadcrumbs'
import ChatBot from 'components/ChatBot/ChatBot'
import { SandboxGoLiveCTA } from 'components/SandboxGoLiveCTA'
import { PathNames, RedirectPathStateKey } from 'constants/General'
import { ApprovalContextProvider } from 'contexts/approvalsContext'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useComplianceViewHelper } from 'hooks/useComplianceViewHelper'
import { useContactCaseCount } from 'hooks/useContactCaseCount'
import { useIsSuspendedAccount } from 'hooks/useIsSuspendedAccount'
import { usePendingApprovalDetails } from 'hooks/usePendingApprovalDetails'
import { useRefreshSession } from 'hooks/useRefreshSession'
import { useTwoFAContext } from 'hooks/useTwoFAContext'
import TwoFactorAuthMandatory from 'pages/TwoFactorAuth/TwoFactorAuthMandatory'
import { RejectedVerificationStatuses, VerificationRequestStatus } from 'types/verification'
import { isLocal, isProduction } from 'utils/environment'
import AppLoader from '../AppLoader'
import Header from '../Header'
import Sidebar from '../Sidebar'

export type PageLayoutProps = {
  title?: string
  isTitleEnabled?: boolean
  loading?: boolean
  canRoute?: boolean
  noStyling?: boolean
  breadcrumbConfig?: CustomBreadCrumbConfig[]
  verificationRequestStatus: VerificationRequestStatus
} & RouteProps

const PageLayout = ({
  title,
  isTitleEnabled = true,
  loading,
  canRoute,
  noStyling,
  breadcrumbConfig,
  verificationRequestStatus,
}: PageLayoutProps) => {
  const { t } = useTranslation()
  const { state, pathname } = useLocation() || {}
  const { isAuthed } = useAuth()
  const { showTwoFAMandatoryScreen } = useTwoFAContext()
  const { enableBanner, enableChatbot } = useFeatureFlags()
  const { profile } = useProfileContext()
  const isComplianceView = useComplianceViewHelper(profile)
  const {
    currentAccount,
    isDeveloperAccount,
    shouldShowUKInfoBanner,
    isLoading: isLoadingAccounts,
  } = useAccountsContext()

  const isSuspendedAccount = useIsSuspendedAccount()

  const currentAccountReference = currentAccount?.reference || ''
  const pendingApprovalDetails = usePendingApprovalDetails(
    currentAccountReference,
    isAuthed && profile?.loggedIn && !!currentAccountReference
  )

  const siteTitle = title ? `${title} | ${process.env.SITE_TITLE}` : `${process.env.SITE_TITLE}`
  useDocumentTitle(siteTitle)

  useRefreshSession()
  useContactCaseCount(isAuthed && profile?.loggedIn)

  if (loading || isLoadingAccounts) {
    return <AppLoader />
  }

  // TODO: Can be move to protected component
  if (!isAuthed) {
    const currentPath = window?.location?.pathname
    return <Navigate to={PathNames.LOGOUT} state={{ [RedirectPathStateKey]: currentPath }} />
  }

  if (
    verificationRequestStatus &&
    RejectedVerificationStatuses.includes(verificationRequestStatus) &&
    pathname !== PathNames.APPLICATION_UNSUCCESSFUL
  ) {
    return <Navigate to={PathNames.APPLICATION_UNSUCCESSFUL} />
  }

  if (canRoute === false) {
    return <Navigate to={PathNames.NOT_FOUND} />
  }

  if (noStyling) {
    return <Outlet />
  }

  const appBanners: AppBannerType[] = []

  if (!isProduction && isDeveloperAccount) {
    appBanners.push(<SandboxGoLiveCTA />)
  }
  if (enableBanner?.enabled) {
    appBanners.push(
      <FlagEnabledBanner
        variant={enableBanner.state === 'critical' ? 'error' : enableBanner.state}
        {...enableBanner}
      />
    )
  }
  if (isSuspendedAccount) {
    appBanners.push(<SuspendedAccountBanner />)
  }

  if (shouldShowUKInfoBanner) {
    appBanners.push(<UKRiskBanner companyName={currentAccount?.name} />)
  }

  if (showTwoFAMandatoryScreen && !state?.isImpersonate) {
    return <TwoFactorAuthMandatory />
  }

  return (
    <>
      <AppBanners banners={appBanners} />
      <Toaster />
      <Box flex minHeight="full" width="full" className="overflow-hidden">
        {!isComplianceView && (
          <ApprovalContextProvider pendingApprovalDetails={pendingApprovalDetails}>
            <Sidebar />
          </ApprovalContextProvider>
        )}

        <Page
          id="page-layout"
          flexCol
          className={clsx('flex-1 relative overflow-y-auto overflow-x-hidden', {
            'mb-24': enableChatbot,
          })}
        >
          <Header isTitleEnabled={isTitleEnabled} breadcrumbConfig={breadcrumbConfig} />

          <Main
            tag="main"
            width="full"
            padding={16}
            className={clsx('sm:p-6 lg:px-8 lg:pb-8 lg:pt-6 max-w-9xl mx-auto', {
              'mb-8': shouldShowUKInfoBanner,
            })}
          >
            <Outlet />
          </Main>

          {shouldShowUKInfoBanner && (
            <p className="text-body-regular-small text-foreground-faint text-center absolute bottom-2 left-0 right-0">
              {t('ukApprovalStamp')}
            </p>
          )}

          {enableChatbot && isAuthed && !isLocal && <ChatBot isAuthed={isAuthed} />}
        </Page>
      </Box>
    </>
  )
}

export default PageLayout
